import { NgClass } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NotificationMessageComponent } from './notification-message/notification-message.component';
import { AuthService } from '@core/services/auth.service';
import { NotificationStatus } from '@core/components/layout/notification/enums/notification-status.enum';
import { SignInResponseModel } from '@core/auth/models/user-login.model';
import { environment } from '@environments/environment';
import { interval, Subscription } from 'rxjs';
import {
  NotificationRequest,
  NotificationWithChecked,
} from './models/notification.model';
import { NotificationService } from './services/notification.service';

@Component({
  selector: 'app-notification',
  imports: [NgClass, NotificationMessageComponent],
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss',
})
export class NotificationComponent implements OnInit, OnDestroy {
  @Input() isNotificationVisible = false;

  @Output() closeNotificationBar = new EventEmitter<boolean>();
  @Output() unViewedNotifications = new EventEmitter<boolean>();

  notifications: NotificationWithChecked[] = [];
  disableButtonActions = false;

  private _userInformation: SignInResponseModel | undefined;
  private _refreshNotifications: Subscription = new Subscription();
  private readonly _refreshNotificationsInterval =
    Number(environment.refreshNotificationsInIntervalsPerMinute) * 60 * 1000;

  private readonly _authService = inject(AuthService);
  private readonly _notificationService = inject(NotificationService);

  ngOnInit(): void {
    this._userInformation = this._authService.getUserInfo()!;
    this.getNotifications();
    this._refreshNotifications = interval(
      this._refreshNotificationsInterval,
    ).subscribe(() => this.getNotifications());
  }

  closeNotifications(): void {
    this.isNotificationVisible = false;
    this.closeNotificationBar.emit(this.isNotificationVisible);
  }

  onCheckedChange(notificationId: number) {
    this.notifications = this.notifications.map((n) =>
      n.notificationId === notificationId ? { ...n, checked: !n.checked } : n,
    );
  }

  hideViewButton(): boolean {
    return this.notifications.some((x) => x.viewed && x.checked);
  }

  existUnViewedNotification(): void {
    this.unViewedNotifications.emit(this.notifications.some((x) => !x.viewed));
  }

  getNotifications(): void {
    this._notificationService
      .getNotifications(this._userInformation!.externalId, undefined)
      .subscribe({
        next: (response) => {
          this.notifications = response
            .map((notifications) => ({
              title: notifications.subject,
              body: notifications.message,
              date: notifications.messageDate,
              notificationId: notifications.notificationId,
              viewed: notifications.messageStatus === NotificationStatus.viewed,
              checked: false,
            }))
            .sort((a, b) => Number(a.viewed) - Number(b.viewed))
            .slice(0, 10);
          this.existUnViewedNotification();
        },
        error: () => (this.notifications = []),
      });
  }

  UpdateNotifications(
    status: NotificationStatus = NotificationStatus.viewed,
  ): void {
    const notificationRead: NotificationRequest[] = this.notifications
      .filter((x) => x.checked)
      .map((n) => ({
        notificationId: n.notificationId,
        messageStatus: status,
      }));

    if (notificationRead.length === 0) return;

    this.disableButtonActions = true;

    this._notificationService
      .updateNotificationStatus(
        this._userInformation!.externalId,
        notificationRead,
      )
      .subscribe({
        next: (resp) => {
          if (resp.statusCode === 200) {
            this.getNotifications();
          }
        },
        complete: () => (this.disableButtonActions = false),
      });
  }

  ngOnDestroy(): void {
    if (this._refreshNotifications) {
      this._refreshNotifications.unsubscribe();
    }
  }
}
